import { DOCUMENT, Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Title } from '@angular/platform-browser';
import { firstValueFrom } from 'rxjs';

/**
 * Identidad de la marca activa: lo que no entra por CSS (textos y rutas de
 * imágenes). Es el `marca.json` del paquete de marca (`src/assets/marcas/<id>/`).
 */
export interface IMarca {
  id: string;
  /** Nombre del sistema para el usuario: encabezado del login. */
  nombre: string;
  /** Título de la ventana y del manifest. */
  titulo: string;
  /** `theme-color` del navegador. */
  themeColor: string;
  /** Archivos de logo dentro del paquete, para fondo claro y oscuro. */
  logo: { claro: string; oscuro: string };
}

/**
 * Ruta fija del paquete de marca. En producción nginx la resuelve por host a
 * `assets/marcas/<id>/`; en `ng serve` lo hace `proxy.conf.js`.
 */
export const RUTA_MARCA = 'assets/marca';

const HORATECH: IMarca = {
  id: 'horatech',
  nombre: 'Plataforma de Utilities',
  titulo: 'Horatech Utilities',
  themeColor: '#113665',
  logo: { claro: 'logo-light.png', oscuro: 'logo-dark.png' },
};

/**
 * Marca activa del portal. Los colores, fuentes y fondos entran solos por
 * `assets/marca/tema.css` y `fuentes.css` (links en `index.html`); acá va lo
 * que los componentes necesitan por código: nombre, título y logos.
 *
 * Se carga en `APP_INITIALIZER`, antes del login: la marca sale del host, no
 * del usuario. Sin `marca.json` (o con error) queda la de horatech.
 */
@Injectable({ providedIn: 'root' })
export class MarcaService {
  private datos: IMarca = HORATECH;

  constructor(
    private http: HttpClient,
    private title: Title,
    @Inject(DOCUMENT) private document: Document,
  ) {}

  public async cargar(): Promise<void> {
    try {
      const marca = await firstValueFrom(
        this.http.get<Partial<IMarca>>(`${RUTA_MARCA}/marca.json`),
      );
      this.datos = { ...HORATECH, ...marca, logo: { ...HORATECH.logo, ...marca.logo } };
    } catch {
      this.datos = HORATECH;
    }
    this.title.setTitle(this.datos.titulo);
    this.document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute('content', this.datos.themeColor);
  }

  public get id(): string {
    return this.datos.id;
  }

  public get nombre(): string {
    return this.datos.nombre;
  }

  public get titulo(): string {
    return this.datos.titulo;
  }

  /** Ruta del logo para el tema activo. */
  public logo(oscuro: boolean): string {
    return `${RUTA_MARCA}/${oscuro ? this.datos.logo.oscuro : this.datos.logo.claro}`;
  }
}
